import { parseEmoji, rasterizeEmoji } from './emoji';

export const STAMP_LIMIT = 80;
export const STAMP_COLORS = [['#244d43', 'Dark green'], ['#fff8e7', 'Cream'], ['#202a31', 'Charcoal'], ['#d47966', 'Terracotta'], ['#5478b0', 'Blue'], ['#d3a431', 'Gold']] as const;
export interface StampContent { text: string; graphemes: number; kind: 'emoji' | 'drawing' }
function segments(text: string) {
  if (typeof Intl.Segmenter !== 'function') throw new Error('This browser cannot read stamps yet. Choose a saved sticker.');
  return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map(entry => entry.segment);
}
export function parseStamp(input: string): StampContent {
  if (input.length > 4096) throw new Error(`Keep your stamp to ${STAMP_LIMIT} characters.`);
  const text = input.normalize('NFC').replace(/\s+/gu, ' ').trim();
  if (!text) throw new Error('Enter an emoji or a few words.');
  if (/[\p{Cc}\p{Cs}\u00ad\u061c\u200b\u200e\u200f\u202a-\u202e\u2060-\u206f\ufeff]/u.test(text)) throw new Error('Remove hidden or control characters from your stamp.');
  const graphemes = segments(text);
  if (graphemes.length > STAMP_LIMIT) throw new Error(`Keep your stamp to ${STAMP_LIMIT} characters.`);
  for (const part of graphemes) {
    if (/[\p{Extended_Pictographic}\p{Regional_Indicator}\p{Emoji_Modifier}]/u.test(part)) parseEmoji(part);
    else if (!/[^\p{M}\p{Cf}]/u.test(part)) throw new Error('Use visible text or a complete emoji.');
  }
  let kind: StampContent['kind'] = 'drawing';
  try { parseEmoji(text); kind = 'emoji'; } catch { /* Mixed content uses the existing transparent drawing flow. */ }
  return { text, graphemes: graphemes.length, kind };
}

/** Wrap by measured words, breaking long words only between complete graphemes. */
export function stampLines(text: string, measure: (line: string) => number, maxWidth: number): string[] {
  const lines: string[] = []; let line = '';
  for (const word of text.split(' ')) {
    const candidate = line ? `${line} ${word}` : word;
    if (measure(candidate) <= maxWidth) { line = candidate; continue; }
    if (line) { lines.push(line); line = ''; }
    for (const part of segments(word)) {
      if (line && measure(line + part) > maxWidth) { lines.push(line); line = ''; }
      line += part;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** The returned PNG is both the live preview and the uploaded asset. */
export async function rasterizeStamp(content: StampContent, color: string): Promise<Blob> {
  if (content.kind === 'emoji') return rasterizeEmoji(content.text);
  if (!/^#[a-f0-9]{6}$/i.test(color)) throw new Error('Choose a text color.');
  const canvas = document.createElement('canvas'), context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not make the stamp. Try a saved sticker.');
  const font = (size: number) => `700 ${size}px system-ui, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
  let size = 160, lines: string[] = [];
  do { context.font = font(size); lines = stampLines(content.text, line => context.measureText(line).width, 960); if (lines.length <= 4) break; size = Math.floor(size * .8); } while (size >= 24);
  const metrics = lines.map(line => context.measureText(line));
  const ascent = Math.max(size * .8, ...metrics.map(metric => metric.actualBoundingBoxAscent || 0));
  const descent = Math.max(size * .2, ...metrics.map(metric => metric.actualBoundingBoxDescent || 0));
  const padding = 32, step = ascent + descent + size * .16;
  const width = Math.ceil(Math.max(...metrics.map(metric => Math.max(metric.width, metric.actualBoundingBoxLeft + metric.actualBoundingBoxRight))) + padding * 2);
  const height = Math.ceil(ascent + descent + step * (lines.length - 1) + padding * 2);
  if (![width,height].every(value=>Number.isFinite(value)&&value>0&&value<=1024)) throw new Error('This stamp is too tall to render. Try fewer words.');
  canvas.width = width; canvas.height = height;
  context.font = font(size); context.fillStyle = color; context.textAlign = 'center'; context.textBaseline = 'alphabetic';
  lines.forEach((line, index) => context.fillText(line, canvas.width / 2, padding + ascent + index * step));
  if (!context.getImageData(0, 0, canvas.width, canvas.height).data.some((value, index) => index % 4 === 3 && value > 0)) throw new Error('That text did not render on this device. Try another stamp.');
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Your stamp could not be made. Try again.')), 'image/png'));
}
